import React, { FunctionComponent, useMemo } from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components/native'

import { Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { MenuItemProps } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/menu/Menu'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanMeasureGroup } from './NpTestPlanMeasureGroup'
import { NpTestPlanMeasureSection } from './NpTestPlanMeasureSection'
import { groupMeasures } from '../data/npTestPlanMeasureUtils'
import {
  NpTestPlanContactGroup,
  NpTestPlanInformant,
  NpTestPlanMeasure,
  NpTestPlanMeasureScope,
  NpTestPlanMeasureType,
} from '../data/npTestPlanTypes'
import { useNpTestPlanMeasureLabels } from '../hooks/useNpTestPlanMeasureLabels'

export interface NpTestPlanBuiltPanelProps {
  contactGroups: NpTestPlanContactGroup[]
  memberName: string
  onAddMeasures: (scope: NpTestPlanMeasureScope) => void
  onRemoveMeasure: (measure: NpTestPlanMeasure, contactGroupId?: string) => void
  onDuplicateGroup: (contactGroupId: string) => void
  onRemoveGroup: (contactGroupId: string) => void
  isRemoveDisabled?: boolean
}

const Sections = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['24px'],
}))

export const NpTestPlanBuiltPanel: FunctionComponent<NpTestPlanBuiltPanelProps> = ({
  contactGroups,
  memberName,
  onAddMeasures,
  onRemoveMeasure,
  onDuplicateGroup,
  onRemoveGroup,
  isRemoveDisabled,
}) => {
  const { formatMessage } = useIntl()
  const { formatInformant, formatMeasureType } = useNpTestPlanMeasureLabels()

  const sections = useMemo(() => groupMeasures(contactGroups, { includeEmpty: true }), [contactGroups])

  const addLabels: Record<NpTestPlanMeasureType, string> = {
    [NpTestPlanMeasureType.RATING_SCALE]: formatMessage({
      defaultMessage: '+ Add scales',
      description: 'Link that opens the library of rating scales that can be added to a neuropsych test plan',
    }),
    [NpTestPlanMeasureType.F2F]: formatMessage({
      defaultMessage: '+ Add tests',
      description: 'Link that opens the library of face-to-face tests that can be added to a neuropsych test plan',
    }),
  }

  const duplicateLabel = formatMessage({
    defaultMessage: 'Duplicate group',
    description: 'Menu option that adds a fresh copy of a group of test measures to a neuropsych test plan',
  })
  const removeLabel = formatMessage({
    defaultMessage: 'Remove group',
    description: 'Menu option that takes a copy of a group of test measures off a neuropsych test plan',
  })

  // A copy can only be removed while another is left, since the last one has no other way back onto the plan.
  const toMenuItems = (contactGroupId: string, canRemove: boolean): MenuItemProps[] => [
    {
      id: 'duplicate',
      text: duplicateLabel,
      onPressItem: () => onDuplicateGroup(contactGroupId),
    },
    ...(canRemove
      ? [
          {
            id: 'remove',
            text: removeLabel,
            onPressItem: () => onRemoveGroup(contactGroupId),
          },
        ]
      : []),
  ]

  return (
    <Sections testID={tID('NpTestPlanBuiltPanel')}>
      {sections.map(({ measureType, groups }) => (
        <NpTestPlanMeasureSection
          key={measureType}
          testIdName={`NpTestPlanBuiltPanel-section-${measureType}`}
          title={formatMeasureType(measureType)}
        >
          {groups.map(({ informant, contactGroupId, measures: grouped }) => {
            const isMember = informant === NpTestPlanInformant.SELF_REPORT
            const copies = groups.filter((group) => group.informant === informant)
            const copyIndex = copies.findIndex((group) => group.contactGroupId === contactGroupId)
            const scopeName = `${measureType}-${informant}${copyIndex ? `-${copyIndex}` : ''}`
            const testIdName = `NpTestPlanBuiltPanel-group-${scopeName}`
            return (
              <NpTestPlanMeasureGroup
                key={`${informant}-${contactGroupId}`}
                testIdName={testIdName}
                title={isMember && memberName ? memberName : formatInformant(informant)}
                subtitle={isMember && memberName ? formatInformant(informant) : undefined}
                // The client completes their own measures, so their group is never copied or removed.
                menuItems={contactGroupId && !isMember ? toMenuItems(contactGroupId, copies.length > 1) : undefined}
                isMenuDisabled={isRemoveDisabled}
                measures={grouped}
                onRemoveMeasure={(measure) => onRemoveMeasure(measure, contactGroupId)}
                isRemoveDisabled={isRemoveDisabled}
                footer={
                  <Link
                    testID={tID(`NpTestPlanBuiltPanel-add-${scopeName}`)}
                    size={BodyTextSize.DEFAULT}
                    text={addLabels[measureType]}
                    onPress={() => onAddMeasures({ measureType, informant, contactGroupId })}
                  />
                }
              />
            )
          })}
        </NpTestPlanMeasureSection>
      ))}
    </Sections>
  )
}
